import jsQR from "jsqr";

interface BarcodeDetection {
  rawValue?: string;
}

interface BarcodeDetectorInstance {
  detect(source: CanvasImageSource): Promise<BarcodeDetection[]>;
}

interface BarcodeDetectorConstructor {
  new (options: { formats: string[] }): BarcodeDetectorInstance;
}

type BrowserWithBarcodeDetector = typeof globalThis & {
  BarcodeDetector?: BarcodeDetectorConstructor;
};

export interface CameraScannerCallbacks {
  onCode: (text: string) => void;
  onError: (error: Error) => void;
}

export class CameraScanner {
  readonly #canvas = document.createElement("canvas");
  readonly #context = this.#canvas.getContext("2d", {
    willReadFrequently: true,
  });
  readonly #callbacks: CameraScannerCallbacks;
  #stream: MediaStream | undefined;
  #video: HTMLVideoElement | undefined;
  #detector: BarcodeDetectorInstance | undefined;
  #animationFrame: number | undefined;
  #running = false;
  #scanning = false;
  #lastScanAt = 0;

  public constructor(callbacks: CameraScannerCallbacks) {
    this.#callbacks = callbacks;
  }

  public async start(video: HTMLVideoElement): Promise<MediaStream> {
    this.stop();
    if (navigator.mediaDevices?.getUserMedia === undefined) {
      throw new Error("This browser does not expose camera access.");
    }
    if (this.#context === null) {
      throw new Error("This browser cannot read camera frames.");
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });
    this.#stream = stream;
    this.#video = video;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();

    const Detector = (globalThis as BrowserWithBarcodeDetector).BarcodeDetector;
    if (Detector !== undefined) {
      try {
        this.#detector = new Detector({ formats: ["qr_code"] });
      } catch {
        this.#detector = undefined;
      }
    }

    this.#running = true;
    this.#schedule();
    return stream;
  }

  public stop(): void {
    this.#running = false;
    this.#scanning = false;
    if (this.#animationFrame !== undefined) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = undefined;
    }
    for (const track of this.#stream?.getTracks() ?? []) {
      track.stop();
    }
    this.#stream = undefined;
    if (this.#video !== undefined) {
      this.#video.pause();
      this.#video.srcObject = null;
      this.#video = undefined;
    }
  }

  #schedule(): void {
    if (!this.#running) {
      return;
    }
    this.#animationFrame = requestAnimationFrame(() => {
      void this.#scan().finally(() => {
        this.#schedule();
      });
    });
  }

  async #scan(): Promise<void> {
    const video = this.#video;
    if (
      !this.#running ||
      this.#scanning ||
      video === undefined ||
      video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
      performance.now() - this.#lastScanAt < 80
    ) {
      return;
    }

    this.#scanning = true;
    this.#lastScanAt = performance.now();
    try {
      const detectorValue = await this.#scanWithDetector(video);
      if (detectorValue !== undefined) {
        this.#callbacks.onCode(detectorValue);
        return;
      }

      const fallbackValue = this.#scanWithJsQr(video);
      if (fallbackValue !== undefined) {
        this.#callbacks.onCode(fallbackValue);
      }
    } catch (error) {
      this.#callbacks.onError(
        error instanceof Error
          ? error
          : new Error("The camera frame could not be scanned."),
      );
    } finally {
      this.#scanning = false;
    }
  }

  async #scanWithDetector(video: HTMLVideoElement): Promise<string | undefined> {
    if (this.#detector === undefined) {
      return undefined;
    }

    try {
      const detections = await this.#detector.detect(video);
      return detections.find(
        (detection) =>
          typeof detection.rawValue === "string" && detection.rawValue.length > 0,
      )?.rawValue;
    } catch {
      this.#detector = undefined;
      return undefined;
    }
  }

  #scanWithJsQr(video: HTMLVideoElement): string | undefined {
    const context = this.#context;
    if (context === null || video.videoWidth <= 0 || video.videoHeight <= 0) {
      return undefined;
    }

    const maximumDimension = 960;
    const scale = Math.min(
      1,
      maximumDimension / Math.max(video.videoWidth, video.videoHeight),
    );
    const width = Math.max(1, Math.round(video.videoWidth * scale));
    const height = Math.max(1, Math.round(video.videoHeight * scale));
    if (this.#canvas.width !== width || this.#canvas.height !== height) {
      this.#canvas.width = width;
      this.#canvas.height = height;
    }
    context.drawImage(video, 0, 0, width, height);
    const image = context.getImageData(0, 0, width, height);
    return jsQR(image.data, width, height, {
      inversionAttempts: "attemptBoth",
    })?.data;
  }
}
